/**
 * Adapter Factory — public API for creating SDK adapters by name.
 *
 * Centralises the adapter-name → SDK-class mapping so that consumers
 * no longer need a local switch statement with dynamic imports.
 */

import type { SdkAdapter } from "./task-runner.js";
import type { GuardrailHooks } from "./guardrail-hooks.js";

export type PermissionMode = "default" | "acceptEdits" | "bypassPermissions" | "plan";

export interface CreateAdapterOptions {
  model?: string;
  cwd?: string;
  tools?: string[];
  permissionMode?: PermissionMode;
  guardrailHooks?: GuardrailHooks;
}

const ADAPTER_PACKAGES: Record<string, string> = {
  claude: "@anthropic-ai/claude-agent-sdk",
  gemini: "@google/adk",
  openai: "@openai/agents",
};

/**
 * Create an SDK adapter instance by name.
 *
 * @param name - One of: "mock", "claude", "openai", "gemini"
 * @param options - Adapter-specific configuration
 */
export async function createAdapter(
  name: string,
  options?: CreateAdapterOptions,
): Promise<SdkAdapter> {
  const { model, cwd, tools, permissionMode, guardrailHooks } = options ?? {};

  switch (name) {
    case "mock": {
      const { MockAdapter } = await import("../adapters/mock.js");
      return new MockAdapter();
    }
    case "claude": {
      try {
        const { ClaudeAgentSdkAdapter } = await import("../adapters/claude-agent-sdk.js");
        return new ClaudeAgentSdkAdapter({
          guardrailHooks,
          ...(model ? { model } : {}),
          ...(cwd ? { cwd } : {}),
          ...(tools ? { tools } : {}),
          ...(permissionMode ? { permissionMode } : {}),
        });
      } catch (err) {
        throwMissingPackageError("claude", err);
      }
    }
    case "gemini": {
      try {
        const { AdkSdkAdapter } = await import("../adapters/adk-sdk.js");
        return new AdkSdkAdapter({
          guardrailHooks,
          ...(model ? { model } : {}),
        });
      } catch (err) {
        throwMissingPackageError("gemini", err);
      }
    }
    case "openai": {
      try {
        const { OpenAIAgentsSdkAdapter } = await import("../adapters/openai-agents-sdk.js");
        return new OpenAIAgentsSdkAdapter({
          guardrailHooks,
          ...(model ? { model } : {}),
        });
      } catch (err) {
        throwMissingPackageError("openai", err);
      }
    }
    default:
      throw new Error(
        `Unknown adapter: "${name}". Available: mock, claude, openai, gemini`,
      );
  }
}

function throwMissingPackageError(adapter: string, err: unknown): never {
  const cause = err as Error;
  if (cause?.message?.includes("Cannot find package")) {
    const pkg = ADAPTER_PACKAGES[adapter];
    throw new Error(
      `Adapter "${adapter}" requires package "${pkg}" which is not installed.\n` +
      `Install it with: npm install ${pkg}`,
    );
  }
  throw cause;
}
